// src/core/sim/types.ts — the client-side mirror of fk-sim's wire types (04 §5.4, 11 §5.1).
//
// `fk-sim-wasm` returns serde-wasm-bindgen objects whose field casing follows the Rust
// derive attributes (ProjectedBoard is camelCase, IntentRef/Intent/ResolutionLog are
// snake_case, enums are strings or `{Variant: payload}`). `./adapt.ts` normalises those into
// the shapes below so the intent layer, HUD and playback code have one vocabulary. The client
// adds nothing to these and must not compute any number that appears on screen.

import type { BoardLook } from '../encounter/look';
export interface Tile { x: number; y: number }

// 04 §6.1
export type Stage = 'Action1' | 'VillainAction' | 'Action2' | 'Bonus' | 'Enemy' | 'Events' | 'Boundary';
// docs/46 T1: the party's whole turn, then the foe's (villain, then enemies), then events
export const STAGE_ORDER: readonly Stage[] = ['Action1', 'Action2', 'Bonus', 'VillainAction', 'Enemy', 'Events'];

// 04 §3.2
export type StatusKind =
  | 'Guarded' | 'Warded' | 'Blessed' | 'Marked' | 'Hidden' | 'Slowed' | 'Rooted'
  | 'Burning' | 'Weakened' | 'Staggered' | 'Stuck' | 'Bleeding' | 'Downed' | 'Injured'
  | 'Frenzied' | 'Arriving' | 'Captive' | 'Raised'
  /** docs/104 PD2: the next foe's blow lands on the paladin instead; magnitude = the paladin's unit id */
  | 'Shielded'
  /** docs/93 AQ8: in deep water its gait cannot enter — hurt each turn, one step out or a hold */
  | 'Floundering'
  /** docs/93 AQ7: a swimmer that struck from under the water, seen and open to blows until it dives */
  | 'Surfaced';
/** `pulses`: turns it still holds, this one included (docs/69 ST2); absent = until cleared. */
export interface StatusTag { kind: StatusKind; magnitude: number; pulses?: number }

export type Side = 'Hero' | 'Ally' | 'Piece' | 'Enemy';

export interface ProjectedBoard {
  snapshotHash: string;            // hex blake3 of the start snapshot
  rulesetVersion: number;
  planHash: string;                // hex hash over sorted intents as the sim sees them now
  units: ProjectedUnit[];          // every unit, start and end-of-pulse state
  effects: ProjectedEffect[];      // one per rendered glyph, in resolution order
  conflicts: ConflictFlag[];       // red/amber, referencing intent ids
  ordering: OrderingNote[];        // where within-stage order changed a result
  declarations: EnemyDeclaration[];// grey layer, already follow-through-adjusted
  hints: HintRung[];               // puzzle hint ladder, if any is active
  wings: WingSummary[];            // crowd rooms: bounds, needs, staffing
  ended?: string;                  // projected encounter outcome, if the pulse would end it
  swayed?: SwayNote[];             // docs/29: every number an encounter modifier changed
}

/** docs/29: an encounter modifier's part in a projected effect — whose plan, on whom, by how much. */
export interface SwayNote { heroId?: number; slot?: Slot; actor?: number; unit: number; modifier: string; delta: number }

export interface ProjectedUnit {
  unitId: number; heroId?: number; owner?: string;   // owner Identity hex, heroes only
  start: Tile; end: Tile;                            // tiles are {x:number,y:number} i32
  hpStart: number; hpEnd: number; hpMax: number;
  dead: boolean; revived?: boolean;                  // #304 reactions: ghost stands back up
  statusesStart?: StatusTag[];                       // statuses as the pulse opens (docs/46 T2)
  statusesEnd: StatusTag[];                          // predicted statuses after the pulse
  side?: Side; name?: string; appearance?: number; onBoardEnd?: boolean;
}

export type SupportGlyph = 'shield' | 'heal' | 'ward' | 'guard' | 'interact';
export type PuzzleGlyph = 'turn' | 'push' | 'wire' | 'flood' | 'countdown';

export type ProjectedEffect =
  | { kind: 'move';    id: IntentRef; path: Tile[]; ghost: Tile; blocked?: Tile; from?: Tile }   // path excludes `from` (docs/26)
  | { kind: 'attack';  id: IntentRef; from: Tile; target: number; /** where the target stands as the blow lands (docs/81 SA2) */ to: Tile; damage: number;
      hpAfter: number; kill: boolean; beam?: boolean;
      /** docs/103 RB4: the blow is flanked — a rogue's Backstab (×2) or a misdirecting rogue's +2 — by `with` on the far side */
      flank?: FlankTag;
      /** the blow shoves its target from `to` (a Shield Bash, a Maul) */
      knockback?: { to: Tile; blockedAt?: Tile } }
  // `from` on the kinds below: where the hero stands as it acts, after its own earlier slots (docs/81 SA2)
  | { kind: 'area';    id: IntentRef; from?: Tile; tiles: Tile[]; hits: AreaHit[] }
  | { kind: 'support'; id: IntentRef; from?: Tile; glyph: SupportGlyph;
      /** `at[i]`: where `beneficiaries[i]` stands as it is helped */
      beneficiaries: number[]; at?: Tile[]; amount?: number; onTile?: Tile }
  | { kind: 'puzzle';  id: IntentRef; from?: Tile; glyph: PuzzleGlyph;
      tiles: Tile[]; value?: number; lit?: boolean }
  | { kind: 'neutral'; id: IntentRef; from?: Tile; substituted: boolean };  // Hold/Defend, or nothing aimed (a Search)

/** docs/103: what a flank did to a blow, and the friend on the far side. */
export type FlankKind = 'backstab' | 'misdirection';
export interface FlankTag { with: number; kind: FlankKind }

/** `at`: where the target stands as the blow lands, before this effect's own shove (docs/81 SA2). */
export interface AreaHit { target: number; at?: Tile; damage: number; hpAfter: number; kill: boolean;
  statuses: StatusTag[]; knockback?: { to: Tile; blockedAt?: Tile } }

export type Slot = 1 | 2 | 'B';
export const SLOTS: readonly Slot[] = [1, 2, 'B'];

/** `fk_sim::StandAt` (docs/81 SA3): where a unit stands as one hero's slot begins — a friend's planning ghost; `fallen` when the party's earlier blows drop it first. */
export interface StandAt { unit: number; tile: Tile; fallen: boolean }
export type IntentState = 'local' | 'provisional' | 'committed' | 'rejected';

export interface IntentRef { heroId: number; slot: Slot; intentId?: number; // undefined = local, unsent
  state: IntentState }

export type ConflictReason = 'path_blocked' | 'target_dead' | 'destination_taken' | 'out_of_range' | 'illegal' | 'moved_by_enemy';
export interface ConflictFlag { severity: 'red' | 'amber'; intent: IntentRef;
  reason: ConflictReason;
  at?: Tile; detail?: string }

export interface OrderingNote { stage: Stage; first: IntentRef; then: IntentRef; why: string }

// ---- 04 §5.4 EnemyDeclaration, shaped for the grey layer ---------------------------------
export type Telegraph = 'open' | 'veiled' | 'hidden';
export type Aim =
  | { kind: 'unit'; target: number }
  | { kind: 'tile'; tile: Tile }
  | { kind: 'region'; tiles: Tile[] }
  | { kind: 'none' };
export type Stance = 'screen' | 'flank' | 'keep_range' | 'hold' | 'brace' | 'lurk';
export interface EnemyDeclaration {
  actor: number;
  telegraph: Telegraph;
  actionName: string;                 // VoiceSlot resolved to its display text
  aim: Aim;
  projected: Array<[unit: number, damage: number]>;  // damage per hero as it will land
  ghost?: Tile;                       // where the actor will stand after its move
  region?: Tile[];                    // veiled telegraph area
  stance?: Stance;
  retargeted?: [from: number, to: number];
  held: boolean;
  leader?: boolean;                   // captain/lieutenant badge
  standingOrder?: string;             // standing-order chip on the leader
  stage?: Stage;
  path?: Tile[];
  tactic?: NoteLine;                  // docs/30: the tactics card behind it, in the sim's words
}

// ---- docs/30 monster minds, in the sim's words (`fk_sim::mind::MindNote`) -----------------
export interface NoteLine { name: string; line: string }
export interface CardCount { name: string; line: string; family: string; count: number; from: string }
export interface MindNote {
  unit: number; name: string; kin: string; doctrine: string;
  wits: NoteLine; mood: NoteLine; nerve: number; prefers: string;
  quirks: NoteLine[]; gear: NoteLine[]; deck: CardCount[]; drawLeft: number;
  last?: NoteLine; grudge?: number; situations: NoteLine[];
  /** docs/31 fog of war: its prowl deck, its senses in words, prowling or hunting (empty when clear). */
  prowl: CardCount[]; senses: string[]; aware?: NoteLine;
  /** docs/32: its focus, the card it is holding to, why its last spell ended. */
  focus?: FocusNote;
  /** docs/115: how keenly it reads the field (0–100) in a word, what it has learned of each hero by watching, and its part in a scheme. */
  insight?: NoteLine; knows: NoteLine[]; part?: NoteLine;
}

// ---- docs/32 focus (`fk_sim::mind::focus::FocusNote`) ---------------------------------------
/** One part of a mind's focus this pulse: its people, a quirk, the weather, its leader, the crowd. */
export interface FocusPart { name: string; value: number }
/** The card a mind is holding to: pulse `pulse` of `of`, at the foe `at` when the card keeps one. */
export interface IntentNote { card: string; line: string; at?: number; pulse: number; of: number; hold: string }
export interface FocusNote { score: number; name: string; line: string; parts: FocusPart[]; intent?: IntentNote; lapse?: NoteLine }

// ---- docs/31 fog of war, as the party sees it (`fk_sim::fog::FogView`, wasm `fog_view`) ------
/** 0 never seen by the party, 1 seen before (explored), 2 in its sight now. */
export type FogCell = 0 | 1 | 2;
/** Why the field is veiled: `night` and `underground` yield to darkvision and light, `weather` to keen eyes. */
export interface FogVeil { key: 'night' | 'underground' | 'weather' | 'rooms'; name: string; line: string }
/** One party unit's senses and the squares it sees (row-major indices), to show each other's sight. */
export interface FogEyes {
  unit: number; sight: number; dark: number; darkvision: boolean; keen: boolean;
  /** Squares its own light reaches (0 = none): a torch or lantern in the dark. */
  light: number; scent: number; tiles: number[]; words: string[];
}
/** A foe out of the party's sight it remembers: last seen there, or only heard. `look` is its Oryx frame (0 = heard). */
export interface FogMark { unit: number; tile: Tile; pulse: number; sense: 'seen' | 'heard' | 'smelled'; look: number }
export interface FogView {
  w: number; h: number; veils: FogVeil[];
  /** Per square, row-major. */
  cells: FogCell[];
  /** 1 where a light falls (torches, lanterns, lava) — only ever set in the dark. */
  lit: (0 | 1)[];
  eyes: FogEyes[]; marks: FogMark[]; pulse: number;
  /**
   * docs/118 PT12: a dungeon crawl's fog — what the party has never seen is no board at all (as
   * behind a puzzle room's closed doors, docs/63): no ground, no outline, no light. Set by the
   * client from the snapshot (`crawl`), not by the sim.
   */
  blind?: boolean;
}

// 06 project_room outputs, minimal shapes
export interface HintRung { rung: 1 | 2 | 3 | 4; tiles: Tile[]; label?: string; value?: string }
export interface WingSummary { wing: number; bounds: Tile[]; needs: string; staffed: boolean; entry?: Tile }

// ---- Intents (04 §5.1) ----------------------------------------------------------------
export type ActionId = number;
export type Direction = 'N' | 'E' | 'S' | 'W' | 'NE' | 'SE' | 'SW' | 'NW';
export type ActionArgs =
  | { kind: 'none' }
  | { kind: 'tile'; tile: Tile }
  | { kind: 'unit'; unit: number }
  | { kind: 'direction'; dir: Direction }
  | { kind: 'unitDir'; unit: number; dir: Direction }
  | { kind: 'line'; from: Tile; dir: Direction }
  | { kind: 'interact'; fixture: number; verb: InteractVerb }  // Interact (06, docs/25 stashes)
  | { kind: 'item'; item: number; target?: number }          // Use Item (docs/40): a bag row, on a friend or (no target) the user
  | { kind: 'raw'; value: unknown };          // anything else: carried through untouched
export type InteractVerb = 'Pull' | 'Push' | 'Open' | 'Examine' | 'Touch' | 'Take' | 'Work' | 'Turn' | 'Place';

export interface Intent {
  heroId: number; unit: number; slot: Slot;
  action: ActionId; args: ActionArgs;
  snapshotHash: string; revision: number;
  substituted: boolean;
}

// 04 §5.2 — the sim's `IllegalReason` variant name (`Requires(..)` and friends are flattened to
// "Requires: AllyAdjacentToTarget" by the adapter).
export type IllegalReason = string;
export type LegalityVerdict =
  | { legal: true; path?: Tile[]; cost: number; note?: string }
  | { legal: false; reason: IllegalReason };

// ---- Snapshot-like (04 §9.2), the subset the presentation shell needs ----------------
/** `Void` (docs/38 BS1): a square of the bounding rectangle outside a free-form field — no board. */
export type Terrain = 'Open' | 'Bad' | 'Wall' | 'Tunnel' | 'Water' | 'Spawn' | 'Door' | 'Deep' | 'Thicket' | 'Chasm' | 'Void';
export interface TileData {
  terrain: Terrain; elev: number; cover: 0 | 1 | number; opaque: boolean;
  exit?: boolean; objective?: boolean; key?: boolean; ice?: boolean;   // ice: docs/55 twist (a pushed block slides)
  hazard?: string;                   // HazardKind variant name (Burning, DeepWater, Fire, Steam)
  trap?: boolean;
  doorOpen?: boolean;
  /** docs/61 EX9: a veil of mist (a `Terrain::Door` that is no door) — the ground is drawn as trail, the mist over it. */
  veil?: boolean;
  /** docs/52: an opened hidden way (a `Terrain::Door` in the wall) — drawn as the gap it is, ground, not a doorway. */
  way?: boolean;
}

// ---- docs/114: interactive ground ------------------------------------------------------------
/** `fk_grammar::Surface`: what lies on a square. */
export type SurfaceName = 'Water' | 'Oil' | 'Grease' | 'Acid' | 'Blood' | 'Ice' | 'Mud' | 'Tar' | 'Web' | 'Honey' | 'Spirits'
  | 'HolyWater' | 'Venom' | 'Slime' | 'Ash' | 'Embers' | 'Powder' | 'Caltrops' | 'Shards' | 'Sap' | 'Leaves' | 'Straw'
  | 'Snow' | 'Brine' | 'Blight' | 'Flour' | 'Rubble' | 'Frost' | 'Glass' | 'Soot';
/** `fk_grammar::Cloud`: what hangs over a square. */
export type CloudName = 'Steam' | 'Smoke' | 'Fumes' | 'Marsh' | 'Dust' | 'Spores' | 'Mist' | 'Incense' | 'Frost' | 'Swarm'
  | 'Pollen' | 'Ash' | 'Cinders' | 'Feathers';
/** `fk_grammar::ObjectKind`: a thing on the field to use. */
export type ThingKind = 'OilBarrel' | 'PowderKeg' | 'WaterBarrel' | 'AleCask' | 'AcidVat' | 'BrewCauldron' | 'HotOilVat'
  | 'Brazier' | 'Campfire' | 'Torch' | 'Lantern' | 'Candles' | 'Chandelier' | 'Boulder' | 'Log' | 'Crate' | 'Haybale'
  | 'Woodpile' | 'Bookshelf' | 'Banner' | 'Beehive' | 'WaspNest' | 'Puffballs' | 'MarshVent' | 'SteamVent' | 'Icicles'
  | 'IcePillar' | 'HolyFont' | 'Well' | 'Trough' | 'AlchemyBench' | 'FlourSacks' | 'MineCart' | 'RottenBeam'
  | 'FireworksCrate' | 'TarBarrel' | 'Bell' | 'Gong' | 'EggSac' | 'DeadTree' | 'Snowman' | 'Scarecrow' | 'BoneHeap'
  | 'Tent' | 'Cart' | 'LightningRod' | 'BlightFont' | 'BrineTub' | 'Hearth' | 'Anvil' | 'Wagon' | 'CoveredWagon'
  | 'HayCart' | 'Wheelbarrow' | 'Rowboat' | 'BarrelRack' | 'CrateStack' | 'Bucket' | 'ButterChurn' | 'Amphorae'
  | 'WineRack' | 'FoodBasket' | 'CheeseWheels' | 'MeatRack' | 'Bed' | 'BunkBed' | 'Table' | 'TableTipped' | 'FeastTable'
  | 'Chair' | 'Bench' | 'Dresser' | 'Wardrobe' | 'DishDresser' | 'Washtub' | 'SpinningWheel' | 'Loom' | 'Rug'
  | 'Tapestry' | 'Painting' | 'Crest' | 'Trophy' | 'ToolRack' | 'Stewpot' | 'BreadOven' | 'ArmourStand' | 'Lectern'
  | 'Pew' | 'Altar' | 'Coffin' | 'Forge' | 'Grindstone' | 'WeaponRack' | 'Workbench' | 'Rake' | 'Haystack'
  | 'ChickenCoop' | 'Dungheap' | 'Plough' | 'ChoppingBlock' | 'Bedroll' | 'LeanTo' | 'SupplyPile' | 'TrainingDummy'
  | 'ArcheryTarget' | 'RoastSpit' | 'Pavilion' | 'Signpost' | 'LampPost' | 'MarketStall' | 'Fountain' | 'Stocks'
  | 'Barricade' | 'WarDrum' | 'Cage' | 'SignalPyre' | 'Nets' | 'RopeCoil' | 'LobsterPots' | 'Anchor' | 'Gravestone'
  | 'FlagPole' | 'WritingDesk' | 'LongTable';
/** `fk_sim::matter::Patch` (docs/114 MT1): a square's matter. `fire`/`charge`: turns left alight / charged. */
export interface MatterPatch { tile: Tile; surface?: SurfaceName; cloud?: CloudName; fire: number; charge: number }
/** `FixtureKind::Object` (docs/114 MT4): a thing on the field. `spent`: broken, tipped or burnt away. */
export interface ThingView { id: number; tile: Tile; kind: ThingKind; lit: boolean; burn: number; spent: boolean; name: string }
/** How a row of the interaction table looks as it fires (`fk_sim::matter::book::look_of`). */
export type ReactLook = 'blast' | 'shock' | 'ignite' | 'douse' | 'cloud' | 'freeze' | 'melt' | 'splash' | 'break' | 'tip'
  | 'roll' | 'fall' | 'toll' | 'collapse' | 'grow' | 'gust' | 'ground' | 'glow' | 'sting' | 'plain';

/** Whether `t` is on the board: inside its rectangle and not `Void` (docs/38 BS1, the sim's `Board::in_bounds`). */
/**
 * docs/63, docs/118 PT12: whether the fog hides the board's very shape where the party has never
 * seen — behind a puzzle room's closed doors, and in a dungeon crawl.
 */
export function blindFog(fog: Pick<FogView, 'veils' | 'blind'> | undefined): boolean {
  return !!fog && (!!fog.blind || fog.veils.some((v) => v.key === 'rooms'));
}
/**
 * docs/63: a square behind a puzzle room's closed doors (or, docs/118 PT12, in a crawl) the party
 * has never seen. The cursor may go there (a teleport may aim into it) but nothing about it is said:
 * the board shows no ground there.
 */
export function unknownSquare(fog: Pick<FogView, 'w' | 'cells' | 'veils' | 'blind'> | undefined, t: Tile): boolean {
  return blindFog(fog) && fog!.cells[t.y * fog!.w + t.x] === 0;
}
/**
 * docs/139 WD5: a square the party has never seen, under any fog. A walk there is judged as if it
 * were open floor (the sim's `fog::hopeful`), so nothing about the ground there is said.
 */
export function neverSeen(fog: Pick<FogView, 'w' | 'cells'> | undefined, t: Tile): boolean {
  return !!fog && t.x >= 0 && t.y >= 0 && t.x < fog.w && fog.cells[t.y * fog.w + t.x] === 0;
}
export function onBoard(board: { w: number; h: number; tiles: ReadonlyArray<{ terrain: Terrain } | undefined> }, t: Tile): boolean {
  return t.x >= 0 && t.y >= 0 && t.x < board.w && t.y < board.h && board.tiles[t.y * board.w + t.x]?.terrain !== 'Void';
}
export type Faction = 'hero' | 'enemy' | 'ally';
export type UnitMark = 'Arriving' | 'Captive' | 'Raised';
export interface UnitState {
  unitId: number; heroId?: number; name: string; faction: Faction;
  tile: Tile; hp: number; hpMax: number; guard: number; initiative: number;
  /** docs/79: a huge unit covers `size × size` squares from `tile`, its origin (top-left); 1 when absent. */
  size?: number;
  /** docs/93 AQ10: how it moves (`stats.mobility.gait`, `fk_sim::Gait` variant name: Foot, Beast, Flier, Swimmer, Burrower, Aquatic); absent = Foot. */
  gait?: string;
  sprite: string;                    // role key into the texture manifest (e.g. 'hero_fighter', 'goblin')
  appearance?: number;               // Oryx creature frame index (heroes 1–5, goblins 253–257)
  classId?: string;                  // heroes
  shapeMark?: number;                // 1..12 radical (11 §5.2 badges)
  leader?: boolean;
  abilities?: number[];              // `stats.abilities` for the hotbar (+ Use Item while the bag holds something, docs/40)
  resource?: { kind: ResourceName; current: number; max: number };
  onBoard?: boolean; downed?: boolean;
  side?: Side;
  /** docs/66: the marks a player must read on the board — Arriving, Captive, Raised. */
  marks?: UnitMark[];
  /** docs/69: every status it carries, in the order the sim set them (Downed and the marks included). */
  statuses?: StatusTag[];
  /** `stats.cooldowns`: ability id → turns it still recovers (it may be used again when this reaches 0). */
  cooldowns?: Record<number, number>;
  /** The numbers its blows are made of (`UnitStats`): main-hand damage, power and focus as Blessed / Weakened leave them, melee reach, missile range. */
  arms?: UnitArms;
  /** Squares it may walk this turn on open ground (`fk_sim::UnitState::move_allowance`: its move, a Dash's bonus, less Slowed; 0 Rooted or Staggered). */
  move?: number;
}
export interface UnitArms { weapon: number; power: number; focus: number; reach: number; range: number; ranged: boolean }
/** `fk_sim::ResourceKind` variant name (Stamina, Mana, Faith, …). */
export type ResourceName = string;
/** A hero's consumables as frozen at load (docs/40 IU1, `fk_sim::Bag`); `used` once Use Item spent it. */
export interface BagView { unit: number; items: Array<{ item: number; def: number; used: boolean }> }
/** A stash on the board (docs/25 D3): a hidden cache is not shown until a hero has found it. */
/** docs/126: `Pouch` is a fallen foe's leftovers (its coin and the pieces it carried), taken by stepping on it. */
/** docs/138 XO7: `Find` is an exploration's small hidden find (`FixtureKind::Find`), picked up underfoot; `opened` = taken. */
export type StashKind = 'Chest' | 'Cache' | 'WarChest' | 'Pouch' | 'Find';
export interface StashView {
  id: number; tile: Tile; kind: StashKind; hidden: boolean; opened: boolean; gold: number; draws: number; name: string;
  /** docs/138 XO7: a find's look (`FIND_LOOKS` index) and the catalogue pieces in it. */
  look?: number; items?: number[];
}
/**
 * docs/41: a puzzle room's mechanism as the party sees it (`fk_sim::FixtureKind` minus stashes).
 * `drives` are the gates or locks it works; a lock's `inputs` are its statues, runes or levers.
 * The lock's answer never reaches the client (the module strikes it from the party's view).
 */
export type FixtureKindName = 'Lever' | 'PressurePlate' | 'Door' | 'Gate' | 'Block' | 'Statue' | 'Rune' | 'Inscription' | 'Lock'
  | 'Passage' | 'Clue' | 'Trap' | 'Relic' | 'Task' | 'Landmark'
  // docs/55: the light, fire, water, carry, way and floor families, bells and dials
  | 'Mirror' | 'Beacon' | 'Eye' | 'Brazier' | 'Spring' | 'Channel' | 'Valve' | 'Wheel' | 'Key' | 'Socket' | 'Portal' | 'Bell' | 'Dial' | 'Pit' | 'Sunk'
  // docs/60: the dungeon crawl
  | 'Stair' | 'Shrine'
  // docs/61: exploration
  | 'Wonder'
  // docs/76: a found hidden door, into a hidden room or onto a stairway down to a cellar
  | 'HiddenDoor'
  // docs/105: a figure that turns to look at another, a figure with empty hands, a thing to give one
  | 'Turntable' | 'Bearer' | 'Idol'
  // docs/106: the lock book's pieces: a thing to touch, a thing on or off, a seat, a jug
  | 'Prop' | 'Switch' | 'Seat' | 'Jug'
  // docs/108: a machine's token (a spindle, a passenger, a cairn, a ring, a hole, a pad, a square)
  | 'Token'
  // docs/140 DT3: a thing that springs a dungeon trap when worked (a lone lever, an idol, a throne)
  | 'Curio';
/** docs/52 HT4: a lair's trap (`fk_sim::TrapKind`); docs/60 DC9 a crawl's chute. */
export type TrapKindName = 'Darts' | 'Pit' | 'Jaws' | 'Rune' | 'Chute';
/** docs/60 DC10: what a shrine gives (`fk_sim::crawl::Boon`). */
export type BoonName = 'Heal' | 'Bless' | 'Ward' | 'Reveal';
export type LockKindName = 'Choose' | 'Order' | 'Pattern' | 'Pairs' | 'Tally' | 'Offering' | 'Arrange' | 'Divine' | 'Measure' | 'Machine';
/** docs/108: `fk_sim::machine::Machine`. */
export type MachineName = 'Stack' | 'Ferry' | 'Bridge' | 'Nim' | 'Rings' | 'Jump' | 'Toads' | 'Slide';
export interface FixtureView {
  id: number; tile: Tile; kind: FixtureKindName; name: string; drives: number[];
  /**
   * Lever raised · plate pressed · gate/door open · rune lit · inscription read · lock solved ·
   * (docs/55) beacon shining · eye lit · brazier burning · channel wet · valve open · wheel turning ·
   * key placed · socket filled · portal awake · bell rung · pit filled. A mirror's `on` is `/`.
   */
  on: boolean;
  threshold?: number; weight?: number; door?: number; needed?: number;
  lock?: LockKindName; inputs?: number[]; progress?: number;
  /**
   * docs/52: a found hidden way (`on` = open; `nook` = the hollow behind it), a found clue, or a
   * trap. A trap the party has not seen through is `known: false` and named by its guise ("a drift
   * of leaves"); the party's view never shows it armed, so only `known` says whether it is live.
   */
  nook?: Tile; points?: number; trap?: TrapKindName; armed?: boolean; known?: boolean;
  /** docs/55: a beacon's direction and the squares its beam lights (physical: always drawn). */
  dir?: Direction; beam?: Tile[];
  /**
   * docs/55: a carried key's bearer (unit id); a dial's face and how many it has; a portal's twin.
   * docs/105: a turntable's face (whom it looks at, among the lock's other figures); a bearer's
   * `partner` is the idol in its hands (`on` = holding); an idol's `holder`, `on` = set in hands.
   */
  holder?: number; face?: number; faces?: number; partner?: number;
  /** docs/106: a jug's size and what it holds (a prop's, switch's, seat's or bearer's look is `look`). */
  cap?: number; holds?: number;
  /** docs/108: a machine lock's machine; a token's value and state (what they mean is the machine's). */
  machine?: MachineName; value?: number; state?: number;
  /** docs/50: a relic's holder (`on` = held); a task's work `done` of `need` (`on` = done); the look index (docs/106: a lock book's dial has one too). */
  done?: number; need?: number; look?: number;
  /** docs/50: a landmark's squares. */
  tiles?: Tile[];
  /** docs/60: a stair's way (`partner` is the stair at its other end); a shrine's boon and the uses it has left (`on` = spent). */
  down?: boolean; boon?: BoonName; uses?: number;
  /** docs/61: a wonder's look (`look` above, `WonderLook` index; `on` = beheld). */
  /** docs/138 XO5: the heroes (unit ids) who have beheld this wonder for themselves (`Wonder.blessed`). */
  blessed?: number[];
  /**
   * docs/76 EE3: a found hidden door (`on` = open; `door` as a way's): `room` is the floor behind
   * it, `below` the stair at the foot of a stairway down (absent for a door into a hidden room).
   */
  room?: Tile[]; below?: number;
  /** docs/140 DT3: a curio's look (`fk_sim::snare::book::Curio` name; `on` = used: it has sprung its trap). */
  curio?: CurioName;
}

// ---- docs/140 dungeon traps (`fk_sim::snare`), as the party sees them --------------------------
/** `fk_sim::snare::book::Curio`: what a trap's curio looks like (and so which verb works it). Order is the sim's. */
export const CURIOS = [
  'Lever', 'SkullLever', 'Rope', 'Idol', 'Throne', 'Fountain', 'Mirror', 'Gong', 'Bell', 'Tome', 'GemStatue', 'Wheel', 'Coffin',
  'Brazier', 'CrystalBall', 'Painting', 'MushroomRing', 'Cask', 'Crown', 'GoldHeap', 'Hourglass', 'SwordInStone', 'Urn',
  'Scarecrow', 'FrozenFace', 'DragonHead', 'Horn', 'Hive', 'Candle', 'Bait', 'TwinStatues', 'RuneCircle', 'Cauldron', 'Well',
  'Mat', 'Vent', 'Cogwheel', 'Plank', 'Pool',
] as const;
export type CurioName = typeof CURIOS[number];
/** `fk_sim::snare::ActKind`: what one beat of a running trap did (`Stopped`/`Undone`: its stop worked, a change put back). */
export type ActKind = 'Bar' | 'Flood' | 'Roll' | 'Warp' | 'Loose' | 'Spill' | 'Haze' | 'Kindle' | 'Volley' | 'Jet' | 'Close' | 'Press'
  | 'Spikes' | 'Crumble' | 'Snuff' | 'Afflict' | 'Shove' | 'Swing' | 'Hurt' | 'Double' | 'Gift' | 'Mend' | 'Alarm' | 'Spin' | 'Swap'
  | 'Polymorph' | 'Hoist' | 'Quake' | 'Shift' | 'Cage' | 'Stopped' | 'Undone';
/**
 * `fk_sim::snare::Trigger` (docs/140 DT3): `Step` a plate, `Wire` a tripwire, `Work` a curio, chest or
 * door worked with `verb`, `Enter`, `Crowd n`, `Linger n`, `Clock n`, `Card n` (a foe's card, reach n),
 * `Slay`, `Loot`, `Gaze n`.
 */
export interface SnareTrigger { kind: 'Step' | 'Wire' | 'Work' | 'Enter' | 'Crowd' | 'Linger' | 'Clock' | 'Card' | 'Slay' | 'Loot' | 'Gaze'; verb?: InteractVerb; n?: number }
export type SnareState = 'Armed' | 'Running' | 'Spent' | 'Disarmed';
/** What a running trap must put back (`fk_sim::snare::Saved`); the party's view clears it. `thing`: the roller's fixture once out. */
export interface SnareSaved {
  ground: { beat: number; tile: Tile; terrain: Terrain }[];
  doors: { fixture: number; inputs: number }[];
  looks: { unit: number; look: number }[];
  thing?: number; at: number; back: boolean; struck: number[];
}
/**
 * `fk_sim::snare::Snare` (docs/140 DT2) as the party sees it (DT11): only once sprung or found;
 * never its landing squares or count, and an armed one without its zone, lanes, creatures, doors,
 * stop or foe. `id` is the truth's (every effect names it).
 */
export interface SnareView {
  id: number; def: number; trigger: SnareTrigger; area: number; anchor: Tile; trip: Tile[]; fixture?: number;
  zone: Tile[]; lanes: { tile: Tile; dir: Direction }[]; to: Tile[]; doors: number[]; brood: number[]; stop?: number; foe?: number;
  /** The storyteller's words when it springs; empty for the book's. */
  line: string;
  state: SnareState; since?: number; known: boolean; victim?: number; count: number; progress: number[]; saved: SnareSaved;
}

// ---- docs/61 exploration, as the party sees it (`fk_sim::explore::Explore`) ------------------
/** What beholding a wonder does; `Unknown` in the party's view until it is beheld. */
export type ExploreGift =
  | { kind: 'Marvel' } | { kind: 'Boon'; boon: BoonName } | { kind: 'Sight' } | { kind: 'Path'; veil: number }
  | { kind: 'Trove'; fixture: number } | { kind: 'Lore' } | { kind: 'Omen' } | { kind: 'Unknown' }
  /** docs/112 UP8: it offers this enchantment (an upgrade id) to the one who beholds it */
  | { kind: 'Enchant'; upgrade: number };
/** A wonder: name blank until its square has been seen; text, omen and gift blank until beheld. */
export interface ExploreWonder {
  fixture: number; name: string; look: number; text: string; gift: ExploreGift; omen: string; found: boolean; by?: number;
  /** docs/138 XO5: every hero (unit id) who has beheld it for themselves, in order; each was offered a blessing. */
  blessed: number[];
}
/** A bank of mist on the `Terrain::Door(door)` squares `tiles`. */
export interface ExploreVeil { door: number; tiles: Tile[]; name: string; parted: boolean }
/** A trail off the board into the mist; name, glimpse and squares blank until sighted, ending until left by. */
export interface ExploreWay { name: string; glimpse: string; ending: string; tiles: Tile[]; sighted: boolean }
/** docs/138 XO9: why the party is here, what the place is, how it ties in (blank on visions from before). */
export interface ExploreBrief { why: string; history: string; ties: string }
/**
 * docs/138 XO8: a camp that keeps to itself. `lair` indexes the truth's `Crawl::lairs` (the party's
 * view keeps only awake lairs, so match it with `campLair`); `kind` the pool's camp, `dress` its
 * `CampDress`. Name, idle words and dressing squares blank while nobody has seen any of it.
 */
export interface ExploreCamp { lair: number; kind: number; dress: number; name: string; idle: string; at: Tile; tiles: Tile[] }
/** docs/138 XO6: a sealed vault: a stair down to a puzzle room with a chest behind its gate. */
export interface ExploreVault { name: string; text: string; stair: number; chest: number; rule: string; floor: Tile[] }
export interface ExploreView {
  /** `fk_schema::Air` index: 0 Mist, 1 Glimmer, 2 Hush, 3 Dream, 4 Bloom, 5 Starlit, 6 Echoing, 7 Rime. */
  air: number;
  wonders: ExploreWonder[]; veils: ExploreVeil[]; ways: ExploreWay[];
  leftBy?: number; ending: string;
  brief: ExploreBrief; camps: ExploreCamp[]; vaults: ExploreVault[];
}

// ---- docs/60 the dungeon crawl, as the party sees it (`fk_sim::crawl::Crawl`) -------------------
/** A level of the crawl: its own rectangle of the board (inclusive). Name and words blank until entered. */
/** `aside`: a place off the crawl's depths (the pit cell) — named and bannered, never counted. */
export interface CrawlDepth { name: string; mood: number; text: string; x0: number; y0: number; x1: number; y1: number; entered: boolean; aside: boolean }
/** A chamber: unnamed until seen, its line blank until entered. */
export interface CrawlArea { name: string; depth: number; entered: boolean; line: string }
/** A woken lair (the party's view keeps only awake ones; `units` only those in the view). */
export interface CrawlLair { name: string; area: number; units: number[]; awake: boolean; line: string }
/** A roamer that has found the party. */
export interface CrawlRoamer { unit: number; engaged: boolean; line: string }
/** 0 sconce, 1 brazier, 2 candles, 3 glow-moss, 4 shrine glow, 5 ember crack (`LightLook`). */
export interface CrawlLight { tile: Tile; radius: number; look: number }
/** A lore fragment on an inscription; `text` blank until read. */
export interface CrawlLore { fixture: number; title: string; text: string; read: boolean }
export interface CrawlChute { trap: number; to: Tile }
export interface CrawlView {
  name: string; hook: string;
  depths: CrawlDepth[]; areas: CrawlArea[];
  /** Row-major over the board: 0 no area, `i + 1` area `i` (0 wherever the party has never seen). */
  areaOf: number[];
  lairs: CrawlLair[]; roamers: CrawlRoamer[]; lights: CrawlLight[]; lore: CrawlLore[]; chutes: CrawlChute[];
  dweller?: number; ending: string; pulseLimit: number;
  /** docs/140: the crawl's dungeon traps the party knows of (sprung or found). */
  snares: SnareView[];
  /** docs/140 DT13: the storyteller's premise (what happened here); empty for a template crawl. */
  premise: string;
  /** docs/140 DT13: the treasure's words, shown when it is taken; the shrine's, read when it answers. */
  treasure: string; shrine: string;
  /** docs/140 DT13: the quest this crawl is a stop of ("Title — the pitch"); empty for a practice crawl. */
  quest: string;
}
/**
 * docs/49 FU: a body on the ground (`fk_sim::Fallen`). It holds no square; `turn` is degrees clockwise
 * from standing and `dx`/`dy` its shift off the square's centre in sixteenths of a square, all
 * seeded by the sim so every client draws it lying the same way.
 */
export interface FallenView { unit: number; tile: Tile; size: number; turn: number; dx: number; dy: number }
/** docs/50 SB2: one aim of the fight as the sim keeps it, with its count where it has one. */
export type GoalKind = 'Rout' | 'BreakLeader' | 'Hold' | 'Reach' | 'Escort' | 'Solve' | 'Survive' | 'Take' | 'Deliver' | 'Work' | 'Depart' | 'Find';
export interface GoalView { kind: GoalKind; fixture?: number; unit?: number; tiles?: Tile[]; value?: number; of?: number }
/** How the aims combine: one, either wins, or all are needed. */
export interface GoalsView { join: 'one' | 'any' | 'all'; goals: GoalView[] }
/** How the fight is lost short of a wipe: the guarded one drops, or the pulse passes. */
export type LossView = { kind: 'Dropped'; unit: number } | { kind: 'After'; pulse: number };
/** docs/90: a change of order as the leader calls it: the new order, why, the hero it is about, the shouted line. */
export interface OrderCall { leader: number; order: string; why: string; at?: number; line: string; pulse: number }

export interface Snapshot {
  encounterId: number; pulseIndex: number; snapshotHash: string; rulesetVersion: number;
  board: { w: number; h: number; tiles: TileData[]; exits: Tile[]; spawn?: Tile[]; kit?: string; seed?: number; hour?: number; weather?: string; season?: string; country?: string; look?: BoardLook; env?: FieldEnv; fallen?: FallenView[]; matter?: MatterPatch[] };  // kit: region kit key (12 §8), seed: view seed for decoration, hour: time of day 0–24 (docs/19 N1), weather: `kind[:intensity[:wind]]` (docs/20 W1), season: spring|summer|autumn|winter (docs/23), country: an outdoor place's country kit and look: rooms' floors and rails (encounter_stage, docs/186 EA13–EA14) — each when the room carries it
  units: UnitState[];
  /** The room's event cards by id → name (`deck.cards`), for the banner when one fires. */
  eventNames?: Record<number, string>;
  order?: string; objective?: string; leaderUnit?: number; pendingSurrender?: boolean; fleeCalled?: boolean;
  orderCall?: OrderCall;             // docs/90: the leader's change of order in the pulse just played, called aloud
  goals?: GoalsView;                 // docs/50: the objective's aims with their progress
  stakes?: string;                   // docs/50: the HUD's line for what wins the fight (`room.stakes`)
  losses?: LossView[];               // docs/50: how the fight is lost short of a wipe
  stashes?: StashView[];             // docs/25: every stash in the room, hidden ones included (draw only `!hidden`)
  fixtures?: FixtureView[];          // docs/41: the room's mechanisms (levers, plates, blocks, statues, runes, inscriptions, gates, the lock)
  things?: ThingView[];              // docs/114 MT4: the things on the field (barrels, braziers, boulders…), spent ones too
  minds?: MindNote[];                // docs/30: every enemy's mind (wasm `minds`)
  fog?: FogView;                     // docs/31: fog of war as the party sees it (wasm `fog_view`); absent = clear field
  bags?: BagView[];                  // docs/40: every hero's consumables (wasm `bags`); absent = nobody carries any
  partyView?: boolean;               // docs/52 HT3: the row is the party's view, not the truth the server resolves (no parity)
  crawl?: CrawlView;                 // docs/60: the dungeon crawl's depths, chambers, lairs, lights and lore (absent off a crawl)
  explore?: ExploreView;             // docs/61: an exploration's wonders, veils and ways (it carries a `crawl` too)
  /** docs/66 RM11: every card scheduled and shown (`deck.pending`, declared), with what it will do. */
  pending?: PendingCard[];
  /** docs/66 RM10: the leader's reserve (`kit_set.reserve`), the only foes who can still join. */
  reserve?: ReserveView[];
  /** docs/66 RM10: how many more times the fallen can be raised. */
  raisings?: number;
  /** docs/66 RM11: the leader still has a second form to show. */
  secondForm?: boolean;
  /** docs/118 PT6: the leader's kit's name (`kit_set.leader`/`solo`), who the objective names while the leader is unseen. */
  bossName?: string;
}

/** docs/66 RM10: one reserve group, as the leader's card shows it. */
export interface ReserveView { role: string; count: number; kin?: string; line: string; from: string; entry: Tile[]; called: boolean }

/** docs/66 RM11: a scheduled card shown a pulse (or more) ahead: its line, when, and what it does. */
export interface PendingCard {
  card: number; firesAt: number; name: string;
  /** `EventKindCard` variant name (Call, Raise, TerrainFails, Hazard, Trap, Weather, Stakes…). */
  kind: string;
  /** Call: the arrival squares; terrain and hazards: the squares it changes. */
  tiles: Tile[];
  /** Raise: who rises, and who raises them. */
  units?: number[]; caster?: number;
  group?: number;
}

// ---- The field (docs/26): what the rules make of the ground, the sky and one unit ----------
/** `board.env` as the rules read it: the land the kit stands for and the stamped sky. */
export interface FieldEnv { land: string; weather: string; intensity: number; season?: string; hour: number; wind: number }
/**
 * `fk_sim::ModifierNote` (docs/29): an encounter modifier in force, in the sim's words. `toward`
 * is the sun's or the wind's side (1 east, −1 west, 0 none).
 */
export interface ModifierNote { modifier: string; key: string; name: string; short: string; rule: string; why: string; toward: number }
export type FieldLine = 'Clear' | 'Veil' | 'Block';
/** One class of ground on this board, priced for the surveyed unit (`cost` null = cannot enter). */
export interface FieldClass { key: string; label: string; cost: number | null; foot: number | null; line: FieldLine; cover: number }
/** `stand: false` — a friend's square the unit only crosses. */
export interface ReachTile { tile: Tile; cost: number; stand: boolean; entangled: boolean; prev: Tile }
/** `fk_sim::Survey`: where a unit can stop and how, the zones gripping it, its sight, the ground. */
export interface Survey {
  unit: number; w: number; pace: number; budget: number; from: Tile;
  reach: ReachTile[];
  zones: { tile: Tile; grip: number }[];
  grips: Tile[];
  sight: number; missilePenalty: number;
  /** Index into `legend` per square, row-major. */
  ground: number[];
  legend: FieldClass[];
  land: string;
  conditions: string[];
  traits: string[];
  /** docs/29: the encounter modifiers in force. */
  modifiers: ModifierNote[];
  /** docs/29 X2: squares the wind adds to a missile at a target [west, level, east] of the unit. */
  windShift: [number, number, number];
  /** docs/93 AQ10: how it moves — the `Gait` variant, its key (`foot`, `swimmer`, `aquatic`…) and its plain words. */
  gait?: string; gaitKey?: string; gaitWords?: string;
  /** docs/93 AQ7: under the surface of deep water (hard to see and hit). */
  submerged?: boolean;
  /** docs/93 AQ8: in deep water its gait cannot enter. */
  floundering?: boolean;
}

/** docs/48 AR2: how the eye meets one square of an action's range. */
export type AimWhy = 'Clear' | 'Blocked' | 'TooFar';
/** `fk_sim::AimRange` (docs/48 AR1): an action's range from where the unit will stand when it acts. */
export interface AimRange { unit: number; action: number; from: Tile; min: number; sight: number; tiles: { tile: Tile; why: AimWhy }[] }

// ---- ResolutionLog (04 §7.1), the mirror the playback plan builder walks ----------------
export type DamageSourceGroup = 'melee' | 'ranged' | 'blast' | 'tick';
export type Effect =
  | { kind: 'Moved'; unit: number; path: Tile[]; blockedAt?: Tile; costs?: number[]; from?: Tile }   // costs: points per step (docs/26, `pace` per open square)
  | { kind: 'Damaged'; unit: number; raw: number; absorbed: number; dealt: number; hpAfter: number; source: DamageSourceGroup }
  | { kind: 'Healed'; unit: number; amount: number; hpAfter: number }
  | { kind: 'Displaced'; unit: number; from: Tile; to: Tile; blockedAt?: Tile }
  | { kind: 'StatusSet'; unit: number; status: StatusKind; magnitude: number; expiresAfter: number }
  | { kind: 'StatusCleared'; unit: number; status: StatusKind }
  | { kind: 'Dropped'; unit: number; by?: number; lies?: FallenView }   // lies: how the body falls (docs/49 FU); none for a Downed hero
  | { kind: 'Revived'; unit: number; hp: number }
  | { kind: 'Spawned'; unit: number; tile: Tile }
  | { kind: 'Held'; unit: number; reason: string }
  | { kind: 'EventFired'; card: number; tiles: Tile[]; title: string }
  | { kind: 'Interacted'; fixture: number; tiles: Tile[]; verb?: InteractVerb; verdict?: 'Accepted' | 'Advanced' | 'Rejected' | 'Unknown' | 'Near'; right?: number; read?: number; moved?: { from: Tile; to: Tile }; opened?: true; taken?: true; placed?: number; beheld?: true; /** docs/140: a curio worked with its verb: its trap answers */ tripped?: true }   // docs/41: what a mechanism made of it (a stash opened, a key or relic taken, `placed`: the key set in this socket)
  | { kind: 'GearPower'; unit: number; power: string; amount: number }   // docs/72 IT3: a worn piece's power changed what just happened
  | { kind: 'Flanked'; unit: number; target: number; with: number; flank: FlankKind }   // docs/103 RB3: a flanked blow, just before its Damaged
  | { kind: 'Interposed'; unit: number; ward: number }   // docs/104 PD2: paladin `unit` took the blow meant for `ward` (the paladin's Damaged follows)
  | { kind: 'Hastened'; unit: number; turns: number }    // docs/104 BD2: an Encore cut `unit`'s cooldowns by `turns`
  | { kind: 'TerrainSet'; terrain: Terrain; tiles: Tile[] }   // the ground changed (docs/104 DR2: a druid's Grow, the entry's actor; docs/96 the ent's thorns)
  | { kind: 'Swayed'; unit: number; modifier: string; delta: number }   // docs/29: a modifier changed the entry before it
  | { kind: 'ItemUsed'; unit: number; item: number; def: number; target: number }   // docs/40: a consumable spent (after its effect)
  | { kind: 'ResourceGained'; unit: number; amount: number; left: number }          // docs/40: a Tonic's refill
  | { kind: 'MoveUsed'; unit: number; id: number; name: string }                     // docs/53: a foe opens a move of the vocabulary
  | { kind: 'Cast'; unit: number; ability: number; from: Tile; aim?: Tile; tiles: Tile[] }   // docs/47: `AbilityUsed`, the cast's geometry as it began
  | { kind: 'Searched'; unit: number; tiles: Tile[]; found: number }                         // docs/52 HT2: a search round the searcher
  | { kind: 'Revealed'; fixture: number; tile: Tile; by?: number; tiles: Tile[] }             // docs/52 HT2: a hidden thing showed itself
  | { kind: 'TrapSprung'; unit: number; fixture: number; tile: Tile; trap: TrapKindName }     // docs/52 HT4: its damage follows
  | { kind: 'Disarmed'; unit: number; fixture: number; tile: Tile; trap: TrapKindName }       // docs/52 HT5
  // docs/60: the dungeon crawl
  | { kind: 'LairWoke'; lair: number; units: number[]; why: string; by?: number }             // a lair woke (`why`: Seen, Entered, DoorOpened, Disturbed, Hurt, Alarm, Heard)
  | { kind: 'Roused'; unit: number; by?: number }                                             // a roamer found the party
  | { kind: 'AreaEntered'; unit: number; area: number; depth?: number }                       // first foot in a chamber (and on a new depth)
  | { kind: 'LoreRead'; unit: number; lore: number }                                          // a lore fragment read
  | { kind: 'ShrineAnswered'; unit: number; fixture: number; boon: BoonName; touched: number[] } // a shrine gave its boon
  | { kind: 'Fell'; unit: number; from: Tile; to: Tile }                                      // a chute dropped a hero into the cell
  // docs/61: exploration
  | { kind: 'Discovered'; unit: number; wonder: number }                                      // a wonder beheld (its gift follows)
  | { kind: 'Blessed'; unit: number; wonder: number }                                         // docs/138 XO5: a hero beheld the wonder for themselves (a blessing offer follows)
  | { kind: 'VeilParted'; veil: number; by?: number }                                         // a bank of mist parted
  | { kind: 'Glimpsed'; unit: number; at: Tile; cells: number }                               // a vantage put land on the map
  | { kind: 'WaySighted'; way: number; by: number }                                           // a way out first seen
  | { kind: 'Departed'; way: number }                                                         // the party went on down a way
  // docs/66 RM10: the leader's call and the raising of the fallen, spoken aloud
  | { kind: 'Called'; leader: number; group: number; line: string; units: number[] }
  | { kind: 'CallBroken'; leader: number; group: number }
  | { kind: 'Raised'; caster: number; units: number[] }
  | { kind: 'RaiseBroken'; caster: number }
  | { kind: 'CaptiveFreed'; unit: number; by?: number }
  // docs/114 MT3: a row of the interaction table fired at `at` (0: matter fading on its own); `set`
  // is every changed square's patch after it, `things` every changed thing; `tiles` the squares it touched
  | { kind: 'Reacted'; rule: number; at: Tile; by?: number; set: MatterPatch[]; things: ThingView[]; tiles: Tile[] }
  // docs/118 PT14: the leader shouts the retreat over the runners (no leader: a voice from the fog);
  // a foe runs `path` from `from` and leaves the board off its end (an empty path: it slips away there)
  | { kind: 'Retreat'; leader?: number; line: string; units: number[] }
  | { kind: 'Left'; unit: number; from: Tile; path: Tile[]; costs?: number[] }
  // docs/126: a fallen foe's pouch lies where it fell; a hero stepped onto one and took it
  | { kind: 'PouchLeft'; unit: number; fixture: number; at: Tile; gold: number; items: number[]; tiles: Tile[] }
  | { kind: 'PickedUp'; unit: number; fixture: number; gold: number; items: number[] }
  // docs/180 RD2: a blow that would have dropped a shielded hero glanced off (`landed` 0: blocked)
  | { kind: 'Glanced'; unit: number; by?: number; landed: number; prevented: number }
  // docs/140: dungeon traps — sprung on `tile` (by a foe's card when `by` is set), one beat acted over
  // `tiles` and `units` (its plain entries follow as their own), over, found by a search, taken apart
  | { kind: 'SnareSprung'; snare: number; def: number; tile: Tile; victim?: number; by?: number }
  | { kind: 'SnareActed'; snare: number; act: ActKind; tiles: Tile[]; units: number[] }
  | { kind: 'SnareEnded'; snare: number }
  | { kind: 'SnareFound'; snare: number; unit: number }
  | { kind: 'SnareDisarmed'; snare: number; unit: number; tile: Tile }
  | { kind: 'Other'; name: string; unit?: number; tiles: Tile[] };

export type Cause =
  | { kind: 'Intent'; ref: IntentRef }
  | { kind: 'Declaration'; actor: number }
  | { kind: 'Reaction'; actor: number; idx: number }
  | { kind: 'Villain'; leader: number; pulseSlot: number }
  | { kind: 'Event'; card: number }
  /** docs/140: a dungeon trap (by `Snare.id`) did it. */
  | { kind: 'Snare'; snare: number }
  | { kind: 'Tick' };

export interface ResolutionEntry { seq: number; stage: Stage; actor?: number; cause: Cause; effect: Effect }
export interface ResolutionLog {
  encounterId: number; pulseIndex: number; rulesetVersion: number;
  snapshotHash: string; planHash: string; endSnapshotHash: string;
  entries: ResolutionEntry[];
  unresolved: Array<[hero: number, slot: Slot, reason: IllegalReason]>;
  unready: number[];
}

export const intentKey = (ref: { heroId: number; slot: Slot }): string => `${ref.heroId}:${ref.slot}`;
export const effectId = (e: ProjectedEffect): string => `${e.kind}:${intentKey(e.id)}`;
export const tileEq = (a: Tile, b: Tile): boolean => a.x === b.x && a.y === b.y;
/** Steps between two squares on the four-way grid (docs/39 FW2): the sim's `dist`. */
export const manhattan = (a: Tile, b: Tile): number => Math.abs(a.x - b.x) + Math.abs(a.y - b.y);
